// src/design/LandingPageBuilder.jsx
// Landing Page Builder — full-screen editor for creating AI-generated landing pages.
// Supports 4 layouts, inline editing, Pexels image search, user upload, color picker.
// Layouts render visually distinct in BOTH edit mode and preview mode.

import React, { useState, useRef, useCallback, useEffect } from 'react';
import PexelsSearchModal from './PexelsSearchModal';
import ModerationWarning from '../components/common/ModerationWarning';
import AiCopyNotice from '../components/common/AiCopyNotice';

const API_URL = process.env.REACT_APP_API_URL;

// Build the article-style markdown export from landing-page sections.
// Intentionally excludes the hero headline + subheadline — those are the
// landing-page banner copy, not blog-post content. Section headlines become
// `##` (H2) so Ghost's title field can own the H1. Empty sections (no
// headline AND no body) are skipped so an in-progress draft doesn't produce
// ghost blocks in the export.
function buildArticleMarkdown(sections) {
  if (!Array.isArray(sections) || sections.length === 0) return '';
  const parts = [];
  for (const s of sections) {
    const headline = (s?.headline || '').trim();
    const body = (s?.body || '').trim();
    if (!headline && !body) continue;
    if (headline) parts.push(`## ${headline}`);
    if (body) parts.push(body);
  }
  return parts.join('\n\n').trim();
}

// ─── Preset accent colors ──────────────────────────────────────────────────
const ACCENT_COLORS = [
  { name: 'Royal Blue', hex: '#2563EB' },
  { name: 'Teal',       hex: '#0D9488' },
  { name: 'Forest',     hex: '#16A34A' },
  { name: 'Burgundy',   hex: '#9F1239' },
  { name: 'Navy',       hex: '#1E3A5F' },
  { name: 'Charcoal',   hex: '#374151' },
  { name: 'Warm Orange', hex: '#EA580C' },
  { name: 'Deep Purple', hex: '#7C3AED' },
  { name: 'Crimson',    hex: '#DC2626' },
  { name: 'Slate',      hex: '#475569' },
];

// ─── CTA options ─────────────────────────────────────────────────────────────
const CTA_OPTIONS = [
  { value: 'contact_us',        label: 'Contact Us' },
  { value: 'call_now',          label: 'Call Now' },
  { value: 'get_quote',         label: 'Get a Free Quote' },
  { value: 'visit_website',     label: 'Visit Our Website' },
  { value: 'book_appointment',  label: 'Book an Appointment' },
  { value: 'event_registration', label: 'Event Registration' },
  { value: 'custom',            label: 'Custom...' },
];

// ─── Default CTA URL resolver ───────────────────────────────────────────────
function getDefaultCtaUrl(ctaType, biz) {
  switch (ctaType) {
    case 'call_now': return biz.phone ? `tel:${biz.phone.replace(/[^+\d]/g, '')}` : '';
    case 'get_quote': return biz.website || '';
    case 'visit_website': return biz.website || '';
    case 'book_appointment': return biz.website || '';
    case 'event_registration': return '';
    case 'custom': return '';
    default: return '';
  }
}

// ─── Layout options ──────────────────────────────────────────────────────────
const LAYOUTS = [
  { key: 'bold-hero',         label: 'Bold Hero',         icon: '◼', desc: 'Full-bleed hero, magazine-style sections' },
  { key: 'grid-gallery',      label: 'Grid Gallery',      icon: '◫', desc: 'Card grid, Pinterest-style showcase' },
  { key: 'feature-strip',     label: 'Feature Strip',     icon: '☰', desc: 'Full-width horizontal bands' },
  { key: 'compact-editorial', label: 'Compact Editorial',  icon: '¶', desc: 'Centered blog-style article' },
];

const LAYOUT_ICON_MAP = {
  'bold-hero': (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 -960 960 960" className="w-4 h-4" fill="currentColor" aria-hidden="true" focusable="false">
      <path d="M312-240q-51 0-97.5-18T131-311q-48-45-69.5-106.5T40-545q0-78 38-126.5T189-720q14 0 26.5 2.5T241-710l239 89 239-89q13-5 25.5-7.5T771-720q73 0 111 48.5T920-545q0 66-21.5 127.5T829-311q-37 35-83.5 53T648-240q-66 0-112-30l-46-30h-20l-46 30q-46 30-112 30Zm0-80q37 0 69-17.5t59-42.5h80q27 25 59 42.5t69 17.5q36 0 69.5-12.5T777-371q34-34 48.5-80t14.5-94q0-41-17-68.5T769-640q-3 0-22 4L480-536 213-636q-5-2-10.5-3t-11.5-1q-37 0-54 27t-17 68q0 49 14.5 95t49.5 80q26 25 59 37.5t69 12.5Zm49-60q37 0 58-16.5t21-45.5q0-49-64.5-93.5T239-580q-37 0-58 16.5T160-518q0 49 64.5 93.5T361-380Zm-6-60q-38 0-82.5-25T220-516q5-2 11.5-3.5T245-521q38 0 82.5 25.5T380-444q-5 2-11.5 3t-13.5 1Zm244 61q72 0 136.5-45t64.5-94q0-29-20.5-46T721-581q-72 0-136.5 45T520-442q0 29 21 46t58 17Zm6-61q-7 0-13-1t-11-3q8-26 52.5-51t82.5-25q7 0 13 1t11 3q-8 26-52.5 51T605-440Zm-125-40Z"/>
    </svg>
  ),
  'grid-gallery': (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 -960 960 960" className="w-4 h-4" fill="currentColor" aria-hidden="true" focusable="false">
      <path d="M120-520v-320h320v320H120Zm0 400v-320h320v320H120Zm400-400v-320h320v320H520Zm0 400v-320h320v320H520ZM200-600h160v-160H200v160Zm400 0h160v-160H600v160Zm0 400h160v-160H600v160Zm-400 0h160v-160H200v160Zm400-400Zm0 240Zm-240 0Zm0-240Z"/>
    </svg>
  ),
  'feature-strip': (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 -960 960 960" className="w-4 h-4" fill="currentColor" aria-hidden="true" focusable="false">
      <path d="M200-400h360v-280H200v280Zm-40 240q-33 0-56.5-23.5T80-240v-480q0-33 23.5-56.5T160-800h640q33 0 56.5 23.5T880-720v480q0 33-23.5 56.5T800-160H160Zm0-80h640v-480H160v480Zm0 0v-480 480Z"/>
    </svg>
  ),
  'compact-editorial': (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 -960 960 960" className="w-4 h-4" fill="currentColor" aria-hidden="true" focusable="false">
      <path d="M300-300h160v-160H300v160Zm0-200h160v-160H300v160Zm200 200h160v-160H500v160Zm0-200h160v-160H500v160ZM160-160q-33 0-56.5-23.5T80-240v-480q0-33 23.5-56.5T160-800h640q33 0 56.5 23.5T880-720v480q0 33-23.5 56.5T800-160H160Zm0-80h640v-480H160v480Zm0 0v-480 480Z"/>
    </svg>
  ),
};

// ─── Utility: lighten a hex color ──────────────────────────────────────────
function lightenColor(hex, amount = 0.92) {
  const num = parseInt(hex.replace('#', ''), 16);
  const r = Math.round((num >> 16) + (255 - (num >> 16)) * amount);
  const g = Math.round(((num >> 8) & 0x00FF) + (255 - ((num >> 8) & 0x00FF)) * amount);
  const b = Math.round((num & 0x0000FF) + (255 - (num & 0x0000FF)) * amount);
  return `rgb(${r},${g},${b})`;
}

// ─── Hero signature card ────────────────────────────────────────────────────
// Always-on identity card rendered below the hero image (NOT overlaid). The
// previous absolute-positioned design collided with the Bold Hero overlay
// headline. Now slots between the hero and the body sections like a business
// signature. Logo + business name + industry + clickable phone, with an
// accent-color left band and accent-color phone button for vibrancy.
function HeroContactStrip({ logoUrl, businessName, industry, phone, accentColor }) {
  if (!businessName && !phone) return null;
  const phoneHref = phone ? `tel:${String(phone).replace(/[^+\d]/g, '')}` : null;
  const accent = accentColor || '#2563EB';
  return (
    <div className="max-w-4xl mx-auto px-6 mt-6">
      <div className="bg-white rounded-xl shadow-md flex items-center gap-4 py-0 pl-4 pr-3 border-l-[5px]"
           style={{ borderLeftColor: accent }}>
        {logoUrl && (
          // eslint-disable-next-line jsx-a11y/img-redundant-alt
          <img src={logoUrl} alt="" className="max-h-16 w-auto object-contain flex-shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          {businessName && (
            <h3 className="text-base font-bold text-gray-900 truncate leading-tight">{businessName}</h3>
          )}
          {industry && (
            <p className="text-xs text-gray-500 truncate leading-tight mt-0.5">{industry}</p>
          )}
        </div>
        {phone && (
          <a href={phoneHref}
             className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-white text-sm font-semibold hover:opacity-90 transition shrink-0"
             style={{ background: accent }}
             onClick={(e) => e.stopPropagation()}>
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
              <path d="M6.62 10.79a15.05 15.05 0 0 0 6.59 6.59l2.2-2.2a1 1 0 0 1 1.05-.24 11.36 11.36 0 0 0 3.58.57 1 1 0 0 1 1 1V20a1 1 0 0 1-1 1A17 17 0 0 1 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1c0 1.25.2 2.45.57 3.58a1 1 0 0 1-.25 1.05l-2.2 2.16Z"/>
            </svg>
            <span className="whitespace-nowrap">{phone}</span>
          </a>
        )}
      </div>
    </div>
  );
}

// ─── Inline editable text component ─────────────────────────────────────────
function EditableText({ value, onChange, className, style, placeholder, multiline, tag: Tag = 'div' }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(value || '');
  const ref = useRef(null);

  useEffect(() => { setText(value || ''); }, [value]);

  useEffect(() => {
    if (editing && ref.current) {
      ref.current.focus();
      if (ref.current.select) ref.current.select();
      if (multiline && ref.current) {
        ref.current.style.height = 'auto';
        ref.current.style.height = ref.current.scrollHeight + 'px';
      }
    }
  }, [editing, multiline]);

  if (!editing) {
    return (
      <Tag
        className={`cursor-pointer hover:ring-2 hover:ring-blue-400 hover:ring-offset-1 rounded transition-all ${className || ''}`}
        style={style}
        onClick={() => setEditing(true)}
        title="Click to edit"
      >
        {value || <span className="opacity-40 italic">{placeholder || 'Click to edit...'}</span>}
      </Tag>
    );
  }

  const save = () => {
    const trimmed = text.trim();
    if (trimmed !== (value || '').trim()) {
      onChange(trimmed);
    }
    setEditing(false);
  };

  if (multiline) {
    return (
      <textarea
        ref={ref}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          e.target.style.height = 'auto';
          e.target.style.height = e.target.scrollHeight + 'px';
        }}
        onBlur={save}
        onKeyDown={(e) => { if (e.key === 'Escape') { setText(value || ''); setEditing(false); } }}
        className={`w-full bg-white/90 border-2 border-blue-400 rounded p-2 outline-none resize-none ${className || ''}`}
        style={{ ...style, minHeight: '3em', overflow: 'hidden' }}
        placeholder={placeholder}
      />
    );
  }

  return (
    <input
      ref={ref}
      type="text"
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={save}
      onKeyDown={(e) => {
        if (e.key === 'Enter') save();
        if (e.key === 'Escape') { setText(value || ''); setEditing(false); }
      }}
      className={`w-full bg-white/90 border-2 border-blue-400 rounded px-2 py-1 outline-none ${className || ''}`}
      style={style}
      placeholder={placeholder}
    />
  );
}

// ─── Image slot component ────────────────────────────────────────────────────
function ImageSlot({ assetId, assetUrl, assetType, onPickImage, onUploadImage, onRemoveImage, accentColor, height = '200px', className = '', rounded = 'rounded-lg' }) {
  const fileInputRef = useRef(null);
  const isVideo = assetType === 'video';

  if (assetUrl) {
    return (
      <div className={`relative isolate group overflow-hidden ${rounded} ${className}`} style={{ height }}>
        {isVideo ? (
          <video src={assetUrl} className="w-full h-full object-cover" autoPlay loop muted playsInline />
        ) : (
          <img src={assetUrl} alt="" className="w-full h-full object-cover" />
        )}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
          <button onClick={onPickImage} className="px-3 py-1.5 bg-white rounded-lg text-xs font-medium shadow-lg hover:bg-gray-50">Search eHandshake</button>
          <button onClick={() => fileInputRef.current?.click()} className="px-3 py-1.5 bg-white rounded-lg text-xs font-medium shadow-lg hover:bg-gray-50">Upload</button>
          <button onClick={onRemoveImage} className="px-3 py-1.5 bg-red-500 text-white rounded-lg text-xs font-medium shadow-lg hover:bg-red-600">Remove</button>
        </div>
        <input ref={fileInputRef} type="file" accept="image/*,video/*" className="hidden"
          onChange={(e) => { const file = e.target.files?.[0]; if (file) onUploadImage(file); e.target.value = ''; }} />
      </div>
    );
  }

  return (
    <div className={`relative border-2 border-dashed border-gray-300 ${rounded} flex flex-col items-center justify-center gap-3 hover:border-blue-400 transition-colors ${className}`} style={{ height, background: '#f9fafb' }}>
      <svg className="w-10 h-10 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0022.5 18.75V5.25A2.25 2.25 0 0020.25 3H3.75A2.25 2.25 0 001.5 5.25v13.5A2.25 2.25 0 003.75 21z" />
      </svg>
      <div className="flex gap-2">
        <button onClick={onPickImage} className="px-3 py-1.5 text-white rounded-lg text-xs font-medium shadow hover:opacity-90" style={{ background: accentColor }}>Search eHandshake</button>
        <button onClick={() => fileInputRef.current?.click()} className="px-3 py-1.5 bg-gray-200 rounded-lg text-xs font-medium hover:bg-gray-300">Upload Image</button>
      </div>
      <input ref={fileInputRef} type="file" accept="image/*" className="hidden"
        onChange={(e) => { const file = e.target.files?.[0]; if (file) onUploadImage(file); e.target.value = ''; }} />
    </div>
  );
}

// ─── Section action buttons (reorder, remove) ──────────────────────────────
function SectionActions({ index, total, onRemove, onMoveUp, onMoveDown }) {
  return (
    <div className="absolute top-2 right-2 z-10 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
      {index > 0 && (
        <button onClick={onMoveUp} className="w-7 h-7 bg-white shadow rounded flex items-center justify-center text-gray-500 hover:text-gray-800" title="Move up">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" /></svg>
        </button>
      )}
      {index < total - 1 && (
        <button onClick={onMoveDown} className="w-7 h-7 bg-white shadow rounded flex items-center justify-center text-gray-500 hover:text-gray-800" title="Move down">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
        </button>
      )}
      <button onClick={onRemove} className="w-7 h-7 bg-red-50 shadow rounded flex items-center justify-center text-red-400 hover:text-red-600 hover:bg-red-100" title="Remove section">
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
      </button>
    </div>
  );
}

// ─── Flyer Attachment Editor ───────────────────────────────────────────────
// Shared component rendered between the section list and the CTA in every
// layout editor. Single optional attachment per landing page; accepts image
// or PDF (PDF rendered server-side to JPEG by /landing-pages/upload-image).
// The aspect ratio of the uploaded file is preserved in both the editor
// preview and the public-page render.
function FlyerAttachmentEditor({ flyerImageUrl, accentColor, onUpload, onRemove }) {
  const fileInputRef = useRef(null);
  return (
    <div className="bg-gray-50 border-t border-gray-200 py-10 px-6">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-4">
          <h3 className="text-sm font-semibold text-gray-800">Attach a Flyer (Optional)</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Add a single image or PDF below the AI sections — e.g. a salon menu, restaurant specials, or printed flyer. Renders full-width with aspect preserved.
          </p>
        </div>
        {flyerImageUrl ? (
          <div className="relative group">
            <img src={flyerImageUrl} alt="" className="w-full h-auto rounded-xl shadow-md" />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 rounded-xl">
              <button onClick={() => fileInputRef.current?.click()} className="px-3 py-1.5 bg-white rounded-lg text-xs font-medium shadow-lg hover:bg-gray-50">Replace</button>
              <button onClick={onRemove} className="px-3 py-1.5 bg-red-500 text-white rounded-lg text-xs font-medium shadow-lg hover:bg-red-600">Remove</button>
            </div>
          </div>
        ) : (
          <div className="border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center gap-3 hover:border-blue-400 transition-colors p-8" style={{ background: '#ffffff' }}>
            <svg className="w-10 h-10 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <button onClick={() => fileInputRef.current?.click()} className="px-4 py-2 text-white rounded-lg text-sm font-medium shadow hover:opacity-90" style={{ background: accentColor }}>
              Attach a Flyer (Image or PDF)
            </button>
            <p className="text-[11px] text-gray-400">JPG · PNG · WebP · GIF · PDF (first page rendered)</p>
          </div>
        )}
        <input ref={fileInputRef} type="file" accept="image/*,application/pdf" className="hidden"
          onChange={(e) => { const file = e.target.files?.[0]; if (file) onUpload(file); e.target.value = ''; }} />
      </div>
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// LAYOUT-SPECIFIC EDITORS — each layout has its own edit-mode renderer
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

// ━━━ LAYOUT 1: BOLD HERO — full-bleed hero, magazine-style alternating sections ━━━
function BoldHeroEditor({ heroImageUrl, heroImageId, heroAssetType, heroHeadline, heroSubheadline, sections, accentColor, ctaText, ctaType, ctaUrl,
  flyerImageUrl, onFlyerUpload, onFlyerRemove,
  businessName, businessPhone, businessIndustry, businessLogoUrl,
  onHeroHeadlineChange, onHeroSubheadlineChange, onHeroPickImage, onHeroUploadImage, onHeroRemoveImage,
  onUpdateSection, onRemoveSection, onMoveSection, onPickSectionImage, onUploadSectionImage,
  onCtaTypeChange, onCtaTextChange, onCtaUrlChange, onCtaClick, debouncedSave, addSectionButtons }) {
  return (
    <div className="min-h-full bg-white">
      {/* FULL-BLEED HERO */}
      <div className="relative z-0 w-full overflow-hidden" style={{ minHeight: '220px' }}>
        <ImageSlot assetUrl={heroImageUrl} assetId={heroImageId} assetType={heroAssetType} onPickImage={onHeroPickImage}
          onUploadImage={onHeroUploadImage} onRemoveImage={onHeroRemoveImage}
          accentColor={accentColor} height="220px" className="w-full" rounded="rounded-none" />
        {heroImageUrl && (
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent flex flex-col justify-end p-10">
            <div className="max-w-4xl mx-auto w-full">
              <EditableText value={heroHeadline} onChange={onHeroHeadlineChange}
                className="text-5xl font-black text-white mb-3 leading-tight" style={{ textShadow: '0 3px 12px rgba(0,0,0,0.6)' }}
                placeholder="Hero headline..." tag="h1" />
              <EditableText value={heroSubheadline} onChange={onHeroSubheadlineChange}
                className="text-xl text-white/90 max-w-2xl" style={{ textShadow: '0 1px 6px rgba(0,0,0,0.4)' }}
                placeholder="Subheadline..." tag="p" />
            </div>
          </div>
        )}
        {!heroImageUrl && (
          <div className="absolute bottom-0 left-0 right-0 px-10 pb-6">
            <div className="max-w-4xl mx-auto">
              <EditableText value={heroHeadline} onChange={onHeroHeadlineChange}
                className="text-4xl font-black text-gray-900 mb-2" placeholder="Hero headline..." tag="h1" />
              <EditableText value={heroSubheadline} onChange={onHeroSubheadlineChange}
                className="text-lg text-gray-500" placeholder="Subheadline..." tag="p" />
            </div>
          </div>
        )}
      </div>

      <HeroContactStrip logoUrl={businessLogoUrl} businessName={businessName} industry={businessIndustry} phone={businessPhone} accentColor={accentColor} />

      {/* ALTERNATING SECTIONS — image left/right, magazine spread style */}
      <div className="relative z-10 max-w-5xl mx-auto py-16 px-8">
        {sections.map((section, i) => {
          const imageOnRight = i % 2 === 1;
          return (
            <div key={`section-${i}`} className={`relative group flex items-stretch gap-10 mb-16 ${imageOnRight ? 'flex-row-reverse' : ''}`}>
              <SectionActions index={i} total={sections.length} onRemove={() => onRemoveSection(i)} onMoveUp={() => onMoveSection(i, -1)} onMoveDown={() => onMoveSection(i, 1)} />
              {/* Image side */}
              <div className="w-5/12 flex-shrink-0">
                <ImageSlot assetUrl={section.image_url} assetId={section.image_asset_id}
                  onPickImage={() => onPickSectionImage(i)} onUploadImage={(f) => onUploadSectionImage(f, i)}
                  onRemoveImage={() => onUpdateSection(i, { image_asset_id: null, image_url: null })}
                  accentColor={accentColor} height="280px" rounded="rounded-2xl" />
              </div>
              {/* Text side */}
              <div className="flex-1 flex flex-col justify-center">
                <div className="w-12 h-1 rounded mb-4" style={{ background: accentColor }} />
                <EditableText value={section.headline} onChange={(v) => onUpdateSection(i, { headline: v })}
                  className="text-2xl font-bold text-gray-900 mb-3" placeholder="Section headline..." tag="h2" />
                <EditableText value={section.body} onChange={(v) => onUpdateSection(i, { body: v })}
                  className="text-base text-gray-600 leading-relaxed" placeholder="Section body text..." multiline tag="p" />
              </div>
            </div>
          );
        })}
      </div>

      {addSectionButtons}

      <FlyerAttachmentEditor flyerImageUrl={flyerImageUrl} accentColor={accentColor}
        onUpload={onFlyerUpload} onRemove={onFlyerRemove} />

      {/* CTA — Full-width accent band */}
      <CTAEditor accentColor={accentColor} ctaText={ctaText} ctaType={ctaType} ctaUrl={ctaUrl}
        onCtaTypeChange={onCtaTypeChange} onCtaTextChange={onCtaTextChange} onCtaUrlChange={onCtaUrlChange}
        onCtaClick={onCtaClick} variant="band" />
    </div>
  );
}

// ━━━ LAYOUT 2: GRID GALLERY — Pinterest-style card grid ━━━━━━━━━━━━━━━━━━
function GridGalleryEditor({ heroImageUrl, heroImageId, heroAssetType, heroHeadline, heroSubheadline, sections, accentColor, ctaText, ctaType, ctaUrl,
  flyerImageUrl, onFlyerUpload, onFlyerRemove,
  businessName, businessPhone, businessIndustry, businessLogoUrl,
  onHeroHeadlineChange, onHeroSubheadlineChange, onHeroPickImage, onHeroUploadImage, onHeroRemoveImage,
  onUpdateSection, onRemoveSection, onMoveSection, onPickSectionImage, onUploadSectionImage,
  onCtaTypeChange, onCtaTextChange, onCtaUrlChange, onCtaClick, debouncedSave, addSectionButtons }) {
  return (
    <div className="min-h-full" style={{ background: '#f3f4f6' }}>
      {/* COMPACT HERO — Centered text with smaller image */}
      <div className="text-center pt-12 pb-8 px-6" style={{ background: `linear-gradient(135deg, ${accentColor}, ${accentColor}dd)` }}>
        <div className="max-w-3xl mx-auto">
          <EditableText value={heroHeadline} onChange={onHeroHeadlineChange}
            className="text-4xl font-black text-white mb-3" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.3)' }}
            placeholder="Hero headline..." tag="h1" />
          <EditableText value={heroSubheadline} onChange={onHeroSubheadlineChange}
            className="text-lg text-white/85" placeholder="Subheadline..." tag="p" />
        </div>
      </div>
      {/* Hero image — smaller, overlapping the banner */}
      <div className="z-0 max-w-4xl mx-auto px-6 -mt-4">
        <ImageSlot assetUrl={heroImageUrl} assetId={heroImageId} assetType={heroAssetType} onPickImage={onHeroPickImage}
          onUploadImage={onHeroUploadImage} onRemoveImage={onHeroRemoveImage}
          accentColor={accentColor} height="260px" className="w-full shadow-xl" rounded="rounded-2xl" />
      </div>

      <HeroContactStrip logoUrl={businessLogoUrl} businessName={businessName} industry={businessIndustry} phone={businessPhone} accentColor={accentColor} />

      {/* CARD GRID — Pinterest/Masonry-ish 2 columns */}
      <div className="relative z-10 max-w-4xl mx-auto py-10 px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {sections.map((section, i) => (
            <div key={`section-${i}`} className="relative group bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden hover:shadow-lg transition-shadow">
              <SectionActions index={i} total={sections.length} onRemove={() => onRemoveSection(i)} onMoveUp={() => onMoveSection(i, -1)} onMoveDown={() => onMoveSection(i, 1)} />
              <ImageSlot assetUrl={section.image_url} assetId={section.image_asset_id}
                onPickImage={() => onPickSectionImage(i)} onUploadImage={(f) => onUploadSectionImage(f, i)}
                onRemoveImage={() => onUpdateSection(i, { image_asset_id: null, image_url: null })}
                accentColor={accentColor} height="200px" rounded="rounded-none" />
              <div className="p-5">
                <EditableText value={section.headline} onChange={(v) => onUpdateSection(i, { headline: v })}
                  className="text-lg font-bold mb-2" style={{ color: accentColor }} placeholder="Section headline..." tag="h2" />
                <EditableText value={section.body} onChange={(v) => onUpdateSection(i, { body: v })}
                  className="text-sm text-gray-600 leading-relaxed" placeholder="Section body text..." multiline tag="p" />
              </div>
            </div>
          ))}
        </div>
      </div>

      {addSectionButtons}

      <FlyerAttachmentEditor flyerImageUrl={flyerImageUrl} accentColor={accentColor}
        onUpload={onFlyerUpload} onRemove={onFlyerRemove} />

      {/* CTA — Rounded pill centered */}
      <CTAEditor accentColor={accentColor} ctaText={ctaText} ctaType={ctaType} ctaUrl={ctaUrl}
        onCtaTypeChange={onCtaTypeChange} onCtaTextChange={onCtaTextChange} onCtaUrlChange={onCtaUrlChange}
        onCtaClick={onCtaClick} variant="pill" />
    </div>
  );
}

// ━━━ LAYOUT 3: FEATURE STRIP — Bold horizontal bands, full-width ━━━━━━━━━
function FeatureStripEditor({ heroImageUrl, heroImageId, heroAssetType, heroHeadline, heroSubheadline, sections, accentColor, ctaText, ctaType, ctaUrl,
  flyerImageUrl, onFlyerUpload, onFlyerRemove,
  businessName, businessPhone, businessIndustry, businessLogoUrl,
  onHeroHeadlineChange, onHeroSubheadlineChange, onHeroPickImage, onHeroUploadImage, onHeroRemoveImage,
  onUpdateSection, onRemoveSection, onMoveSection, onPickSectionImage, onUploadSectionImage,
  onCtaTypeChange, onCtaTextChange, onCtaUrlChange, onCtaClick, debouncedSave, addSectionButtons }) {
  return (
    <div className="min-h-full bg-white">
      {/* HERO — Split: image left, text right on accent bg */}
      <div className="relative z-0 flex min-h-[380px]">
        <div className="w-1/2 flex-shrink-0">
          <ImageSlot assetUrl={heroImageUrl} assetId={heroImageId} assetType={heroAssetType} onPickImage={onHeroPickImage}
            onUploadImage={onHeroUploadImage} onRemoveImage={onHeroRemoveImage}
            accentColor={accentColor} height="100%" className="w-full h-full" rounded="rounded-none" />
        </div>
        <div className="w-1/2 flex flex-col justify-center px-12 py-10" style={{ background: accentColor }}>
          <EditableText value={heroHeadline} onChange={onHeroHeadlineChange}
            className="text-4xl font-black text-white mb-4 leading-tight" placeholder="Hero headline..." tag="h1" />
          <EditableText value={heroSubheadline} onChange={onHeroSubheadlineChange}
            className="text-lg text-white/80 leading-relaxed" placeholder="Subheadline..." tag="p" />
        </div>
      </div>

      <HeroContactStrip logoUrl={businessLogoUrl} businessName={businessName} industry={businessIndustry} phone={businessPhone} accentColor={accentColor} />

      {/* HORIZONTAL BANDS — Alternating white / light gray, full-width */}
      {sections.map((section, i) => {
        const isOdd = i % 2 === 1;
        const bgColor = isOdd ? '#f9fafb' : '#ffffff';
        return (
          <div key={`section-${i}`} className="relative z-10 group" style={{ background: bgColor }}>
            <SectionActions index={i} total={sections.length} onRemove={() => onRemoveSection(i)} onMoveUp={() => onMoveSection(i, -1)} onMoveDown={() => onMoveSection(i, 1)} />
            <div className={`max-w-5xl mx-auto px-8 py-14 flex items-center gap-12 ${isOdd ? 'flex-row-reverse' : ''}`}>
              {/* Image */}
              <div className="w-2/5 flex-shrink-0">
                <ImageSlot assetUrl={section.image_url} assetId={section.image_asset_id}
                  onPickImage={() => onPickSectionImage(i)} onUploadImage={(f) => onUploadSectionImage(f, i)}
                  onRemoveImage={() => onUpdateSection(i, { image_asset_id: null, image_url: null })}
                  accentColor={accentColor} height="240px" rounded="rounded-xl" />
              </div>
              {/* Text */}
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-4">
                  <span className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold" style={{ background: accentColor }}>{i + 1}</span>
                  <EditableText value={section.headline} onChange={(v) => onUpdateSection(i, { headline: v })}
                    className="text-2xl font-bold text-gray-900" placeholder="Section headline..." tag="h2" />
                </div>
                <EditableText value={section.body} onChange={(v) => onUpdateSection(i, { body: v })}
                  className="text-base text-gray-600 leading-relaxed pl-11" placeholder="Section body text..." multiline tag="p" />
              </div>
            </div>
          </div>
        );
      })}

      {addSectionButtons}

      <FlyerAttachmentEditor flyerImageUrl={flyerImageUrl} accentColor={accentColor}
        onUpload={onFlyerUpload} onRemove={onFlyerRemove} />

      {/* CTA — Inverted colors (white text on accent bg) */}
      <CTAEditor accentColor={accentColor} ctaText={ctaText} ctaType={ctaType} ctaUrl={ctaUrl}
        onCtaTypeChange={onCtaTypeChange} onCtaTextChange={onCtaTextChange} onCtaUrlChange={onCtaUrlChange}
        onCtaClick={onCtaClick} variant="inverted" />
    </div>
  );
}

// ━━━ LAYOUT 4: COMPACT EDITORIAL — Narrow centered blog-style article ━━━━
function CompactEditorialEditor({ heroImageUrl, heroImageId, heroAssetType, heroHeadline, heroSubheadline, sections, accentColor, ctaText, ctaType, ctaUrl,
  flyerImageUrl, onFlyerUpload, onFlyerRemove,
  businessName, businessPhone, businessIndustry, businessLogoUrl,
  onHeroHeadlineChange, onHeroSubheadlineChange, onHeroPickImage, onHeroUploadImage, onHeroRemoveImage,
  onUpdateSection, onRemoveSection, onMoveSection, onPickSectionImage, onUploadSectionImage,
  onCtaTypeChange, onCtaTextChange, onCtaUrlChange, onCtaClick, debouncedSave, addSectionButtons }) {
  return (
    <div className="min-h-full bg-white">
      <div className="max-w-2xl mx-auto pt-12 px-6">
        {/* ARTICLE-STYLE HERO — Image first, then title like a blog post */}
        <div className="relative z-0">
          <ImageSlot assetUrl={heroImageUrl} assetId={heroImageId} assetType={heroAssetType} onPickImage={onHeroPickImage}
            onUploadImage={onHeroUploadImage} onRemoveImage={onHeroRemoveImage}
            accentColor={accentColor} height="340px" className="w-full shadow-lg" rounded="rounded-2xl" />
        </div>
      </div>

      <HeroContactStrip logoUrl={businessLogoUrl} businessName={businessName} industry={businessIndustry} phone={businessPhone} accentColor={accentColor} />

      <div className="max-w-2xl mx-auto pt-6 px-6">

        <div className="mb-2">
          <span className="inline-block px-3 py-1 text-xs font-semibold rounded-full text-white" style={{ background: accentColor }}>FEATURED</span>
        </div>
        <EditableText value={heroHeadline} onChange={onHeroHeadlineChange}
          className="text-3xl font-extrabold text-gray-900 mb-3 leading-tight" placeholder="Article title..." tag="h1" />
        <EditableText value={heroSubheadline} onChange={onHeroSubheadlineChange}
          className="text-lg text-gray-500 mb-6" placeholder="Subtitle..." tag="p" />

        <div className="w-full h-px bg-gray-200 mb-10" />

        {/* ARTICLE SECTIONS — flowing text, inline images below each paragraph */}
        {sections.map((section, i) => (
          <div key={`section-${i}`} className="relative z-10 group mb-12">
            <SectionActions index={i} total={sections.length} onRemove={() => onRemoveSection(i)} onMoveUp={() => onMoveSection(i, -1)} onMoveDown={() => onMoveSection(i, 1)} />
            <EditableText value={section.headline} onChange={(v) => onUpdateSection(i, { headline: v })}
              className="text-xl font-bold mb-3" style={{ color: accentColor }} placeholder="Section headline..." tag="h2" />
            <EditableText value={section.body} onChange={(v) => onUpdateSection(i, { body: v })}
              className="text-base text-gray-700 leading-7 mb-4" placeholder="Write your content..." multiline tag="p" />
            <ImageSlot assetUrl={section.image_url} assetId={section.image_asset_id}
              onPickImage={() => onPickSectionImage(i)} onUploadImage={(f) => onUploadSectionImage(f, i)}
              onRemoveImage={() => onUpdateSection(i, { image_asset_id: null, image_url: null })}
              accentColor={accentColor} height="220px" rounded="rounded-xl" />
          </div>
        ))}

        {addSectionButtons}

        <FlyerAttachmentEditor flyerImageUrl={flyerImageUrl} accentColor={accentColor}
          onUpload={onFlyerUpload} onRemove={onFlyerRemove} />

        {/* CTA — Simple inline text-style */}
        <CTAEditor accentColor={accentColor} ctaText={ctaText} ctaType={ctaType} ctaUrl={ctaUrl}
          onCtaTypeChange={onCtaTypeChange} onCtaTextChange={onCtaTextChange} onCtaUrlChange={onCtaUrlChange}
          onCtaClick={onCtaClick} variant="inline" />

        <div className="h-12" />
      </div>
    </div>
  );
}

// ─── CTA Editor — different visual variants per layout ──────────────────────
function CTAEditor({ accentColor, ctaText, ctaType, ctaUrl, onCtaTypeChange, onCtaTextChange, onCtaUrlChange, onCtaClick, variant }) {
  const showUrlField = ctaType !== 'contact_us';
  const urlRequired = ['get_quote', 'visit_website', 'book_appointment', 'event_registration', 'custom'].includes(ctaType);
  const phoneRequired = ctaType === 'call_now';
  const urlMissing = showUrlField && !ctaUrl.trim() && (urlRequired || phoneRequired);

  const handleButtonClick = () => {
    if (onCtaClick) onCtaClick();
  };

  const renderControls = () => (
    <div className="flex flex-col items-center gap-1 mt-4 max-w-lg mx-auto">
      <div className="flex flex-wrap items-center gap-3 w-full">
        <select value={ctaType} onChange={(e) => onCtaTypeChange(e.target.value)}
          className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs outline-none focus:border-blue-400 bg-white">
          {CTA_OPTIONS.map((opt) => (<option key={opt.value} value={opt.value}>{opt.label}</option>))}
        </select>
        <input type="text" value={ctaText} onChange={(e) => onCtaTextChange(e.target.value)}
          className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs outline-none focus:border-blue-400 flex-1 min-w-[120px] bg-white" placeholder="Button text..." />
        {showUrlField && (
          <input type="text" value={ctaUrl} onChange={(e) => onCtaUrlChange(e.target.value)}
            className={`border rounded-lg px-2 py-1.5 text-xs outline-none flex-1 min-w-[140px] bg-white ${urlMissing ? 'border-red-400 ring-1 ring-red-300' : 'border-gray-300 focus:border-blue-400'}`}
            placeholder={phoneRequired ? 'Phone number required' : 'URL required — https://...'} />
        )}
      </div>
      {urlMissing && (
        <p className="text-[11px] text-red-500 font-medium self-end">
          {phoneRequired ? '⚠ Enter a phone number to publish' : '⚠ Enter a URL to publish'}
        </p>
      )}
    </div>
  );

  if (variant === 'band') {
    return (
      <div className="py-14 text-center" style={{ background: `${accentColor}12` }}>
        <button onClick={handleButtonClick} className="px-10 py-4 text-white font-bold rounded-xl shadow-lg text-lg cursor-pointer hover:opacity-90 transition" style={{ background: accentColor }}>
          {ctaText || 'Contact Us'}
        </button>
        {renderControls()}
      </div>
    );
  }

  if (variant === 'pill') {
    return (
      <div className="py-12 text-center bg-white rounded-2xl max-w-4xl mx-auto mb-8 shadow-sm border border-gray-100">
        <button onClick={handleButtonClick} className="px-12 py-4 text-white font-bold rounded-full shadow-lg text-lg cursor-pointer hover:opacity-90 transition" style={{ background: accentColor }}>
          {ctaText || 'Contact Us'}
        </button>
        {renderControls()}
      </div>
    );
  }

  if (variant === 'inverted') {
    return (
      <div className="py-16 text-center" style={{ background: accentColor }}>
        <button onClick={handleButtonClick} className="px-10 py-4 bg-white font-bold rounded-xl shadow-lg text-lg cursor-pointer hover:opacity-90 transition" style={{ color: accentColor }}>
          {ctaText || 'Contact Us'}
        </button>
        {renderControls()}
      </div>
    );
  }

  // inline — editorial style
  return (
    <div className="text-center py-10 border-t border-gray-200">
      <button onClick={handleButtonClick} className="px-8 py-3 text-white font-semibold rounded-lg shadow-md text-base cursor-pointer hover:opacity-90 transition" style={{ background: accentColor }}>
        {ctaText || 'Contact Us'}
      </button>
      {renderControls()}
    </div>
  );
}

// ─── Layout Editor Switcher ─────────────────────────────────────────────────
function LayoutEditor(props) {
  switch (props.layout) {
    case 'grid-gallery':      return <GridGalleryEditor {...props} />;
    case 'feature-strip':     return <FeatureStripEditor {...props} />;
    case 'compact-editorial': return <CompactEditorialEditor {...props} />;
    default:                  return <BoldHeroEditor {...props} />;
  }
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// PREVIEW-ONLY LAYOUT RENDERERS (read-only, no edit chrome)
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

function BoldHeroPreview({ heroImageUrl, heroAssetType, heroHeadline, heroSubheadline, sections, accentColor, ctaText, onCtaClick, businessName, businessPhone, businessIndustry, businessLogoUrl }) {
  const isVideo = heroAssetType === 'video';
  return (
    <div className="min-h-full bg-white">
      {heroImageUrl ? (
        <div className="relative w-full" style={{ minHeight: '220px' }}>
          {isVideo ? (
            <video src={heroImageUrl} className="w-full object-cover" style={{ height: '220px' }} autoPlay loop muted playsInline />
          ) : (
            <img src={heroImageUrl} alt="" className="w-full object-cover" style={{ height: '220px' }} />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent flex flex-col justify-end p-10">
            <div className="max-w-4xl mx-auto w-full">
              <h1 className="text-5xl font-black text-white mb-3 leading-tight" style={{ textShadow: '0 3px 12px rgba(0,0,0,0.6)' }}>{heroHeadline}</h1>
              <p className="text-xl text-white/90 max-w-2xl" style={{ textShadow: '0 1px 6px rgba(0,0,0,0.4)' }}>{heroSubheadline}</p>
            </div>
          </div>
        </div>
      ) : (
        <div className="py-20 px-10 text-center" style={{ background: accentColor }}>
          <h1 className="text-5xl font-black text-white mb-3">{heroHeadline}</h1>
          <p className="text-xl text-white/80 max-w-2xl mx-auto">{heroSubheadline}</p>
        </div>
      )}
      <HeroContactStrip logoUrl={businessLogoUrl} businessName={businessName} industry={businessIndustry} phone={businessPhone} accentColor={accentColor} />
      <div className="max-w-5xl mx-auto py-16 px-8">
        {sections.map((section, i) => (
          <div key={i} className={`flex items-stretch gap-10 mb-16 ${i % 2 === 1 ? 'flex-row-reverse' : ''}`}>
            {section.image_url && (
              <div className="w-5/12 flex-shrink-0">
                <img src={section.image_url} alt="" className="w-full h-full rounded-2xl object-cover shadow-md" style={{ maxHeight: '280px' }} />
              </div>
            )}
            <div className={`flex-1 flex flex-col justify-center ${!section.image_url ? 'max-w-3xl mx-auto' : ''}`}>
              <div className="w-12 h-1 rounded mb-4" style={{ background: accentColor }} />
              <h2 className="text-2xl font-bold text-gray-900 mb-3">{section.headline}</h2>
              <p className="text-base text-gray-600 leading-relaxed">{section.body}</p>
            </div>
          </div>
        ))}
      </div>
      <div className="py-14 text-center" style={{ background: `${accentColor}12` }}>
        <button onClick={onCtaClick} className="px-10 py-4 text-white font-bold rounded-xl shadow-lg text-lg cursor-pointer hover:opacity-90 transition" style={{ background: accentColor }}>{ctaText || 'Contact Us'}</button>
      </div>
    </div>
  );
}

function GridGalleryPreview({ heroImageUrl, heroAssetType, heroHeadline, heroSubheadline, sections, accentColor, ctaText, onCtaClick, businessName, businessPhone, businessIndustry, businessLogoUrl }) {
  const isVideo = heroAssetType === 'video';
  return (
    <div className="min-h-full" style={{ background: '#f3f4f6' }}>
      <div className="text-center pt-12 pb-8 px-6" style={{ background: `linear-gradient(135deg, ${accentColor}, ${accentColor}dd)` }}>
        <h1 className="text-4xl font-black text-white mb-3" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.3)' }}>{heroHeadline}</h1>
        <p className="text-lg text-white/85 max-w-2xl mx-auto">{heroSubheadline}</p>
      </div>
      {heroImageUrl && (
        <div className="max-w-4xl mx-auto px-6 -mt-4">
          {isVideo ? (
            <video src={heroImageUrl} className="w-full rounded-2xl shadow-xl object-cover" style={{ maxHeight: '280px' }} autoPlay loop muted playsInline />
          ) : (
            <img src={heroImageUrl} alt="" className="w-full rounded-2xl shadow-xl object-cover" style={{ maxHeight: '280px' }} />
          )}
        </div>
      )}
      <HeroContactStrip logoUrl={businessLogoUrl} businessName={businessName} industry={businessIndustry} phone={businessPhone} accentColor={accentColor} />
      <div className="max-w-4xl mx-auto py-10 px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {sections.map((section, i) => (
            <div key={i} className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden">
              {section.image_url && <img src={section.image_url} alt="" className="w-full h-48 object-cover" />}
              <div className="p-5">
                <h2 className="text-lg font-bold mb-2" style={{ color: accentColor }}>{section.headline}</h2>
                <p className="text-sm text-gray-600 leading-relaxed">{section.body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="py-12 text-center">
        <button onClick={onCtaClick} className="px-12 py-4 text-white font-bold rounded-full shadow-lg text-lg cursor-pointer hover:opacity-90 transition" style={{ background: accentColor }}>{ctaText || 'Contact Us'}</button>
      </div>
    </div>
  );
}

function FeatureStripPreview({ heroImageUrl, heroAssetType, heroHeadline, heroSubheadline, sections, accentColor, ctaText, onCtaClick, businessName, businessPhone, businessIndustry, businessLogoUrl }) {
  const isVideo = heroAssetType === 'video';
  return (
    <div className="min-h-full bg-white">
      <div className="flex min-h-[380px]">
        {heroImageUrl ? (
          <div className="w-1/2 flex-shrink-0">
            {isVideo ? (
              <video src={heroImageUrl} className="w-full h-full object-cover" style={{ minHeight: '380px' }} autoPlay loop muted playsInline />
            ) : (
              <img src={heroImageUrl} alt="" className="w-full h-full object-cover" style={{ minHeight: '380px' }} />
            )}
          </div>
        ) : (
          <div className="w-1/2 flex-shrink-0" style={{ background: `${accentColor}20`, minHeight: '380px' }} />
        )}
        <div className="w-1/2 flex flex-col justify-center px-12 py-10" style={{ background: accentColor }}>
          <h1 className="text-4xl font-black text-white mb-4 leading-tight">{heroHeadline}</h1>
          <p className="text-lg text-white/80 leading-relaxed">{heroSubheadline}</p>
        </div>
      </div>
      <HeroContactStrip logoUrl={businessLogoUrl} businessName={businessName} industry={businessIndustry} phone={businessPhone} accentColor={accentColor} />
      {sections.map((section, i) => {
        const isOdd = i % 2 === 1;
        return (
          <div key={i} style={{ background: isOdd ? '#f9fafb' : '#ffffff' }}>
            <div className={`max-w-5xl mx-auto px-8 py-14 flex items-center gap-12 ${isOdd ? 'flex-row-reverse' : ''}`}>
              {section.image_url && (
                <div className="w-2/5 flex-shrink-0">
                  <img src={section.image_url} alt="" className="w-full rounded-xl shadow-md object-cover" style={{ maxHeight: '240px' }} />
                </div>
              )}
              <div className={`flex-1 ${!section.image_url ? 'max-w-3xl mx-auto' : ''}`}>
                <div className="flex items-center gap-3 mb-4">
                  <span className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold" style={{ background: accentColor }}>{i + 1}</span>
                  <h2 className="text-2xl font-bold text-gray-900">{section.headline}</h2>
                </div>
                <p className="text-base text-gray-600 leading-relaxed pl-11">{section.body}</p>
              </div>
            </div>
          </div>
        );
      })}
      <div className="py-16 text-center" style={{ background: accentColor }}>
        <button onClick={onCtaClick} className="px-10 py-4 bg-white font-bold rounded-xl shadow-lg text-lg cursor-pointer hover:opacity-90 transition" style={{ color: accentColor }}>{ctaText || 'Contact Us'}</button>
      </div>
    </div>
  );
}

function CompactEditorialPreview({ heroImageUrl, heroAssetType, heroHeadline, heroSubheadline, sections, accentColor, ctaText, onCtaClick, businessName, businessPhone, businessIndustry, businessLogoUrl }) {
  const isVideo = heroAssetType === 'video';
  return (
    <div className="min-h-full bg-white">
      {heroImageUrl && (
        <div className="max-w-2xl mx-auto pt-12 px-6">
          {isVideo ? (
            <video src={heroImageUrl} className="w-full rounded-2xl shadow-lg object-cover" style={{ maxHeight: '340px' }} autoPlay loop muted playsInline />
          ) : (
            <img src={heroImageUrl} alt="" className="w-full rounded-2xl shadow-lg object-cover" style={{ maxHeight: '340px' }} />
          )}
        </div>
      )}
      <HeroContactStrip logoUrl={businessLogoUrl} businessName={businessName} industry={businessIndustry} phone={businessPhone} accentColor={accentColor} />
      <div className="max-w-2xl mx-auto pt-6 px-6">
        <span className="inline-block px-3 py-1 text-xs font-semibold rounded-full text-white mb-2" style={{ background: accentColor }}>FEATURED</span>
        <h1 className="text-3xl font-extrabold text-gray-900 mb-3 leading-tight">{heroHeadline}</h1>
        <p className="text-lg text-gray-500 mb-6">{heroSubheadline}</p>
        <div className="w-full h-px bg-gray-200 mb-10" />
        {sections.map((section, i) => (
          <div key={i} className="mb-12">
            <h2 className="text-xl font-bold mb-3" style={{ color: accentColor }}>{section.headline}</h2>
            <p className="text-base text-gray-700 leading-7 mb-4">{section.body}</p>
            {section.image_url && <img src={section.image_url} alt="" className="w-full rounded-xl object-cover shadow-sm" style={{ maxHeight: '260px' }} />}
          </div>
        ))}
        <div className="text-center py-10 border-t border-gray-200">
          <button onClick={onCtaClick} className="px-8 py-3 text-white font-semibold rounded-lg shadow-md text-base cursor-pointer hover:opacity-90 transition" style={{ background: accentColor }}>{ctaText || 'Contact Us'}</button>
        </div>
      </div>
    </div>
  );
}

function LayoutPreview(props) {
  switch (props.layout) {
    case 'grid-gallery':      return <GridGalleryPreview {...props} />;
    case 'feature-strip':     return <FeatureStripPreview {...props} />;
    case 'compact-editorial': return <CompactEditorialPreview {...props} />;
    default:                  return <BoldHeroPreview {...props} />;
  }
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// MAIN COMPONENT
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
export default function LandingPageBuilder({ landingPageId, initialData, onClose, onPublished }) {
  // ─── State ────────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(!initialData);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [moderationAlert, setModerationAlert] = useState(null);

  const [layout, setLayout] = useState(initialData?.layout || 'bold-hero');
  const [accentColor, setAccentColor] = useState(initialData?.accent_color || '#2563EB');
  const [hexInput, setHexInput] = useState('');
  const [heroHeadline, setHeroHeadline] = useState(initialData?.hero_headline || '');
  const [heroSubheadline, setHeroSubheadline] = useState(initialData?.hero_subheadline || '');
  const [heroImageId, setHeroImageId] = useState(initialData?.hero_image_id || null);
  const [heroImageUrl, setHeroImageUrl] = useState(initialData?.hero_image_url || null);
  const [heroAssetType, setHeroAssetType] = useState(initialData?.hero_asset_type || 'image');
  const [sections, setSections] = useState(initialData?.sections || []);
  // Flyer attachment — optional single image (or rendered PDF first page)
  // that displays below the AI sections and above the CTA on every layout.
  // Stored as designcenter_landing_pages.flyer_image_id (FK into assets).
  const [flyerImageId, setFlyerImageId] = useState(initialData?.flyer_image_id || null);
  const [flyerImageUrl, setFlyerImageUrl] = useState(
    initialData?.flyer_image_id
      ? `${API_URL}/api/designcenter/assets/${initialData.flyer_image_id}/file`
      : null
  );

  // Ref always tracks the latest sections — prevents stale closures in async handlers
  const sectionsRef = useRef(sections);
  sectionsRef.current = sections;
  const [ctaType, setCtaType] = useState(initialData?.cta_type || 'contact_us');
  const [ctaText, setCtaText] = useState(initialData?.cta_text || 'Contact Us');
  const [ctaUrl, setCtaUrl] = useState(initialData?.cta_url || '');
  const [slug, setSlug] = useState(initialData?.slug || '');
  const [showContactToast, setShowContactToast] = useState(false);
  // Article-export confirmation. `{ message, isError }` while visible, null
  // when dismissed. Auto-clears after a few seconds (see effect below).
  const [exportNotice, setExportNotice] = useState(null);

  // Business info for CTA actions + hero contact strip
  const [bizInfo, setBizInfo] = useState({
    name: '', address: '', city: '', state: '', zip: '',
    phone: '', website: '', email: '', logo: '', industry: '',
  });

  // Pexels modal state
  const [showPexels, setShowPexels] = useState(false);
  const [pexelsTarget, setPexelsTarget] = useState(null);
  const [pexelsInitialQuery, setPexelsInitialQuery] = useState('');

  const [addingSectionAI, setAddingSectionAI] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const scrollRef = useRef(null);

  // ─── Always fetch business info for CTA defaults ─────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/designcenter/business-info`, { credentials: 'include' });
        const data = await res.json();
        if (data.ok && data.bizInfo) {
          setBizInfo(prev => {
            // Only update if current bizInfo is empty (avoid overwriting data from landing page load)
            if (!prev.phone && !prev.website && !prev.email) return data.bizInfo;
            return prev;
          });
        }
      } catch (err) {
        console.error('[LandingPageBuilder] failed to load business info:', err);
      }
    })();
  }, []);

  // ─── When bizInfo arrives, set default CTA URL if still empty ───────────
  useEffect(() => {
    if (!bizInfo.phone && !bizInfo.website) return; // no biz data yet
    // If ctaUrl is empty and ctaType needs a default, fill it in
    setCtaUrl(prev => {
      if (prev) return prev; // already set, don't override
      const defaultUrl = getDefaultCtaUrl(ctaType, bizInfo);
      return defaultUrl || prev;
    });
  }, [bizInfo, ctaType]);

  // ─── Load data if not provided ────────────────────────────────────────────
  useEffect(() => {
    if (initialData || !landingPageId) return;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/designcenter/landing-pages/${landingPageId}`, { credentials: 'include' });
        const data = await res.json();
        if (data.ok && data.landingPage) {
          const lp = data.landingPage;
          setLayout(lp.layout || 'bold-hero');
          setAccentColor(lp.accent_color || '#2563EB');
          setHeroHeadline(lp.hero_headline || '');
          setHeroSubheadline(lp.hero_subheadline || '');
          setHeroImageId(lp.hero_image_id);
          // Reconstruct hero image URL from asset ID (the DB stores only the ID)
          if (lp.hero_image_id) {
            setHeroImageUrl(`${API_URL}/api/designcenter/assets/${lp.hero_image_id}/file`);
          }
          // Flyer attachment (optional)
          setFlyerImageId(lp.flyer_image_id || null);
          setFlyerImageUrl(
            lp.flyer_image_id
              ? `${API_URL}/api/designcenter/assets/${lp.flyer_image_id}/file`
              : null
          );
          // Reconstruct section image URLs from asset IDs (the DB stores only the ID)
          const sectionsWithUrls = (lp.sections_json || []).map(s => ({
            ...s,
            image_url: s.image_asset_id
              ? `${API_URL}/api/designcenter/assets/${s.image_asset_id}/file`
              : null,
          }));
          setSections(sectionsWithUrls);
          setCtaType(lp.cta_type || 'contact_us');
          setCtaText(lp.cta_text || 'Contact Us');
          setCtaUrl(lp.cta_url || '');
          setSlug(lp.slug || '');

          // Store business info for CTA defaults + hero contact strip
          const biz = {
            name: lp.business_name || '',
            address: lp.business_address || '',
            city: lp.business_city || '',
            state: lp.business_state || '',
            zip: lp.business_zip || '',
            phone: lp.business_phone || '',
            website: lp.business_website || '',
            email: lp.user_email || '',
            logo: lp.business_logo || '',
            industry: lp.business_industry || '',
          };
          setBizInfo(biz);

          // Set default CTA URL if not already set
          if (!lp.cta_url) {
            const defaultUrl = getDefaultCtaUrl(lp.cta_type || 'contact_us', biz);
            if (defaultUrl) setCtaUrl(defaultUrl);
          }
        }
      } catch (err) {
        setError('Failed to load landing page');
      }
      setLoading(false);
    })();
  }, [landingPageId, initialData]);

  // ─── Auto-save on changes (debounced) ─────────────────────────────────────
  const saveTimer = useRef(null);
  const save = useCallback(async (overrides = {}) => {
    if (!landingPageId) return;
    setSaving(true);
    setModerationAlert(null);
    try {
      const res = await fetch(`${API_URL}/api/designcenter/landing-pages/${landingPageId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          layout: overrides.layout ?? layout,
          hero_image_id: overrides.hero_image_id ?? heroImageId,
          hero_headline: overrides.hero_headline ?? heroHeadline,
          hero_subheadline: overrides.hero_subheadline ?? heroSubheadline,
          sections_json: overrides.sections_json ?? sections,
          cta_type: overrides.cta_type ?? ctaType,
          cta_text: overrides.cta_text ?? ctaText,
          cta_url: overrides.cta_url ?? ctaUrl,
          accent_color: overrides.accent_color ?? accentColor,
          flyer_image_id: overrides.flyer_image_id ?? flyerImageId,
        }),
      });
      const data = await res.json();
      if (data.moderation) {
        setModerationAlert({
          moderationId: data.moderationId,
          categories: data.categories,
          severity: data.severity,
          reason: data.reason,
        });
      }
    } catch (err) {
      console.error('[LandingPageBuilder] save error:', err);
    }
    setSaving(false);
  }, [landingPageId, layout, heroImageId, heroHeadline, heroSubheadline, sections, ctaType, ctaText, ctaUrl, accentColor, flyerImageId]);

  const debouncedSave = useCallback((overrides = {}) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => save(overrides), 1500);
  }, [save]);

  // ─── Publish ──────────────────────────────────────────────────────────────
  const handlePublish = async () => {
    if (!landingPageId) return;

    // Validate CTA: URL-based types require a valid URL
    const urlRequiredTypes = ['get_quote', 'visit_website', 'book_appointment', 'event_registration', 'custom'];
    if (urlRequiredTypes.includes(ctaType) && !ctaUrl.trim()) {
      setError('Please enter a URL for your CTA button before publishing.');
      return;
    }
    if (ctaType === 'call_now' && !ctaUrl.trim() && !bizInfo.phone) {
      setError('Please enter a phone number for your Call Now button before publishing.');
      return;
    }

    setPublishing(true);
    setError('');
    try {
      await save();
      const res = await fetch(`${API_URL}/api/designcenter/landing-pages/${landingPageId}/publish`, {
        method: 'POST', credentials: 'include',
      });
      const data = await res.json();
      if (data.ok) {
        setSuccessMsg('Landing page published!');
        onPublished?.(data.landingPage);
      } else {
        setError(data.error || 'Publish failed');
      }
    } catch (err) {
      setError('Network error publishing');
    }
    setPublishing(false);
  };

  // ─── Section operations (use sectionsRef to avoid stale closures in async handlers) ──
  const updateSection = (index, updated) => {
    const current = sectionsRef.current;
    const next = [...current];
    next[index] = { ...next[index], ...updated };
    setSections(next);
    sectionsRef.current = next;
    debouncedSave({ sections_json: next });
  };

  const removeSection = (index) => {
    const current = sectionsRef.current;
    const next = current.filter((_, i) => i !== index).map((s, i) => ({ ...s, order: i + 1 }));
    setSections(next);
    sectionsRef.current = next;
    debouncedSave({ sections_json: next });
  };

  const moveSection = (index, direction) => {
    const next = [...sectionsRef.current];
    const swapIdx = index + direction;
    if (swapIdx < 0 || swapIdx >= next.length) return;
    [next[index], next[swapIdx]] = [next[swapIdx], next[index]];
    const reordered = next.map((s, i) => ({ ...s, order: i + 1 }));
    setSections(reordered);
    sectionsRef.current = reordered;
    debouncedSave({ sections_json: reordered });
  };

  const addBlankSection = () => {
    const current = sectionsRef.current;
    const next = [...current, { order: current.length + 1, headline: '', body: '', image_asset_id: null, image_url: null, image_search_term: '' }];
    setSections(next);
    sectionsRef.current = next;
    debouncedSave({ sections_json: next });
  };

  const addSectionAI = async () => {
    if (!landingPageId) return;
    setAddingSectionAI(true);
    try {
      const res = await fetch(`${API_URL}/api/designcenter/landing-pages/${landingPageId}/add-section`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}),
      });
      const data = await res.json();
      if (data.ok && data.section) {
        const next = [...sectionsRef.current, { ...data.section, image_url: null }];
        setSections(next);
        sectionsRef.current = next;
        debouncedSave({ sections_json: next });
      }
    } catch (err) {
      console.error('[LandingPageBuilder] add section AI error:', err);
    }
    setAddingSectionAI(false);
  };

  // ─── Image handling ───────────────────────────────────────────────────────
  const openPexelsFor = (target, searchTerm) => {
    setPexelsTarget(target);
    setPexelsInitialQuery(searchTerm || '');
    setShowPexels(true);
  };

  const handleUploadImage = async (file, target) => {
    console.log('[LandingPageBuilder] 📤 Upload starting:', { target, fileName: file.name, fileSize: file.size });
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await fetch(`${API_URL}/api/designcenter/landing-pages/upload-image`, {
        method: 'POST', credentials: 'include', body: formData,
      });
      const data = await res.json();
      console.log('[LandingPageBuilder] 📤 Upload response:', data);
      if (data.ok) {
        const imageUrl = `${API_URL}/uploads/designcenter/${data.file_relpath}`;
        if (target === 'hero') {
          console.log('[LandingPageBuilder] 🖼 Setting hero image:', imageUrl);
          setHeroImageId(data.asset_id);
          setHeroImageUrl(imageUrl);
          debouncedSave({ hero_image_id: data.asset_id });
        } else if (target?.sectionIndex !== undefined) {
          console.log(`[LandingPageBuilder] 🖼 Setting section ${target.sectionIndex} image:`, imageUrl);
          console.log(`[LandingPageBuilder]    Current sections count: ${sectionsRef.current.length}`);
          console.log(`[LandingPageBuilder]    Section ${target.sectionIndex} before:`, JSON.stringify(sectionsRef.current[target.sectionIndex]));
          updateSection(target.sectionIndex, { image_asset_id: data.asset_id, image_url: imageUrl });
          console.log(`[LandingPageBuilder]    Section ${target.sectionIndex} after:`, JSON.stringify(sectionsRef.current[target.sectionIndex]));
        } else if (target === 'flyer') {
          console.log('[LandingPageBuilder] 📎 Setting flyer attachment:', imageUrl);
          setFlyerImageId(data.asset_id);
          setFlyerImageUrl(imageUrl);
          debouncedSave({ flyer_image_id: data.asset_id });
        }
      } else {
        console.error('[LandingPageBuilder] ❌ Upload failed:', data.error || 'Unknown error');
      }
    } catch (err) {
      console.error('[LandingPageBuilder] ❌ Upload error:', err);
    }
  };

  const handleFlyerRemove = () => {
    setFlyerImageId(null);
    setFlyerImageUrl(null);
    debouncedSave({ flyer_image_id: null });
  };

  const handlePexelsImported = (importedAssets) => {
    // importedAssets is an array from pexels/import response: [{ asset_id, url, thumb_url, ... }]
    if (importedAssets?.length > 0) {
      const asset = importedAssets[0]; // landing page uses single image per slot
      const imageUrl = `${API_URL}${asset.url}`;
      const target = pexelsTarget;

      if (target === 'hero') {
        setHeroImageId(asset.asset_id);
        setHeroImageUrl(imageUrl);
        debouncedSave({ hero_image_id: asset.asset_id });
      } else if (target?.sectionIndex !== undefined) {
        updateSection(target.sectionIndex, { image_asset_id: asset.asset_id, image_url: imageUrl });
      }
    }
    setShowPexels(false);
    setPexelsTarget(null);
    setPexelsInitialQuery('');
  };

  // ─── Color change helpers ─────────────────────────────────────────────────
  const applyColor = (hex) => {
    setAccentColor(hex);
    debouncedSave({ accent_color: hex });
  };

  const applyHexInput = () => {
    const cleaned = hexInput.trim().replace(/^#/, '');
    if (/^[0-9a-fA-F]{6}$/.test(cleaned)) {
      applyColor(`#${cleaned}`);
      setHexInput('');
    }
  };

  // ─── CTA change handlers ──────────────────────────────────────────────────
  const handleCtaTypeChange = (type) => {
    setCtaType(type);
    const opt = CTA_OPTIONS.find(o => o.value === type);
    if (opt && type !== 'custom') setCtaText(opt.label);
    const defaultUrl = getDefaultCtaUrl(type, bizInfo);
    setCtaUrl(defaultUrl);
    debouncedSave({
      cta_type: type,
      cta_text: type !== 'custom' ? opt?.label : ctaText,
      cta_url: defaultUrl,
    });
  };

  const handleCtaTextChange = (text) => {
    setCtaText(text);
    debouncedSave({ cta_text: text });
  };

  const handleCtaUrlChange = (url) => {
    setCtaUrl(url);
    debouncedSave({ cta_url: url });
  };

  // ─── Export as Article ────────────────────────────────────────────────────
  // Wraps the section headlines + body text as markdown and gives the user
  // both a clipboard copy AND a .md file download. Hero headline +
  // subheadline are intentionally excluded — they're landing-page banner
  // copy, not blog-post content. Ghost (and any other markdown-capable
  // editor) accepts the pasted output directly, so `## Section` becomes a
  // real H2 inside the blog post rather than competing with the post title.
  const handleExportAsArticle = async () => {
    const md = buildArticleMarkdown(sectionsRef.current);
    if (!md) {
      setExportNotice({ message: 'Add some section content first.', isError: true });
      return;
    }

    // Clipboard — best-effort. Some browsers block clipboard writes without
    // a recent user gesture or over HTTP; the .md download still happens.
    let clipboardOk = false;
    try {
      await navigator.clipboard.writeText(md);
      clipboardOk = true;
    } catch (e) {
      console.warn('[export-article] clipboard write failed:', e);
    }

    // File download. Filename derived from the page slug first (already
    // URL-safe), falls back to the business name (sanitized to ASCII), then
    // a generic 'article' if neither is available.
    const filenameBase = (slug || bizInfo.name || 'article')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60) || 'article';
    const blob = new Blob([md], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filenameBase}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);

    setExportNotice({
      message: clipboardOk
        ? 'Copied to clipboard + downloaded as .md file.'
        : 'Downloaded as .md file. (Clipboard blocked by browser.)',
      isError: false,
    });
  };

  // Auto-dismiss the export confirmation toast after 4 seconds.
  useEffect(() => {
    if (!exportNotice) return undefined;
    const t = setTimeout(() => setExportNotice(null), 4000);
    return () => clearTimeout(t);
  }, [exportNotice]);

  const handleCtaClick = () => {
    if (ctaType === 'contact_us') {
      setShowContactToast(true);
    } else if (ctaType === 'call_now' && ctaUrl) {
      window.open(ctaUrl.startsWith('tel:') ? ctaUrl : `tel:${ctaUrl}`, '_self');
    } else if (ctaUrl) {
      window.open(ctaUrl.startsWith('http') ? ctaUrl : `https://${ctaUrl}`, '_blank');
    }
  };

  // ─── Loading state ────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="fixed inset-0 z-[9999] bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-gray-500 text-sm">Loading landing page...</p>
        </div>
      </div>
    );
  }

  // ━━━ RENDER ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  return (
    <div className="fixed inset-0 z-[9999] p-[3px]" style={{ background: '#00aef7' }}>
    <div className="w-full h-full bg-gray-50 flex flex-col overflow-hidden rounded-sm">

      {/* ─── Top toolbar ─────────────────────────────────────────────────── */}
      <div className="flex-none bg-white border-b border-gray-200 px-4 py-2.5 flex items-center gap-4">

        {/* Back button */}
        <button onClick={onClose} className="flex items-center gap-1.5 text-gray-600 hover:text-gray-900 text-sm font-medium">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
          Back
        </button>

        <div className="w-px h-6 bg-gray-200" />

        {/* Layout picker */}
        <div className="flex items-center gap-1.5">

          {LAYOUTS.map((l) => (
            <button
              key={l.key}
              onClick={() => { setLayout(l.key); debouncedSave({ layout: l.key }); }}
              className={`px-2.5 py-1 text-xs rounded-md font-medium transition-colors ${
                layout === l.key
                  ? 'text-white shadow-sm'
                  : 'text-gray-500 bg-gray-100 hover:bg-gray-200'
              }`}
              style={layout === l.key ? { background: accentColor } : {}}
              title={`${l.label}: ${l.desc}`}
              aria-label={l.label}
              >
                {LAYOUT_ICON_MAP[l.key]}
                <span className="sr-only">{l.label}</span>
              </button>
          ))}
        </div>

        <div className="w-px h-6 bg-gray-200" />

        {/* Color picker */}
        <div className="flex items-center gap-1">
          {ACCENT_COLORS.map((c) => (
            <button
              key={c.hex}
              onClick={() => applyColor(c.hex)}
              className={`w-5 h-5 rounded-full border-2 transition-transform hover:scale-110 ${
                accentColor === c.hex ? 'border-gray-800 scale-110' : 'border-gray-300'
              }`}
              style={{ background: c.hex }}
              title={c.name}
            />
          ))}
          <div className="flex items-center ml-1.5">
            <span className="text-gray-400 text-xs mr-0.5">#</span>
            <input
              type="text" value={hexInput}
              onChange={(e) => setHexInput(e.target.value.replace(/[^0-9a-fA-F]/g, '').slice(0, 6))}
              onKeyDown={(e) => { if (e.key === 'Enter') applyHexInput(); }}
              onBlur={applyHexInput}
              placeholder="HEX"
              className="w-16 text-xs border border-gray-300 rounded px-1.5 py-0.5 outline-none focus:border-blue-400"
              maxLength={6}
            />
          </div>
        </div>

        <div className="flex-1" />

        {/* Saving indicator removed — it flashed in next to the Preview/Publish
            buttons during layout switches and shifted them right for 3-4 seconds.
            Save is debounced + always succeeds; the user doesn't need the spinner. */}
        {successMsg && <span className="text-xs text-green-600 font-medium">{successMsg}</span>}
        {error && <span className="text-xs text-red-500">{error}</span>}

        <button onClick={() => setShowPreview(!showPreview)}
          className="px-3 py-1.5 text-xs font-medium bg-gray-100 rounded-lg hover:bg-gray-200 text-gray-700">
          {showPreview ? 'Edit' : 'Preview'}
        </button>

        <button onClick={handlePublish} disabled={publishing}
          className="px-4 py-1.5 text-xs font-semibold text-white rounded-lg shadow-sm hover:opacity-90 disabled:opacity-50 whitespace-nowrap"
          style={{ background: accentColor }}>
          {publishing ? 'Publishing...' : 'Publish to Sharing Center'}
        </button>
      </div>

      {/* ─── Moderation warning ──────────────────────────────────────────── */}
      <ModerationWarning moderation={moderationAlert} onDismiss={() => setModerationAlert(null)} />

      {/* ─── Editor / Preview body ────────────────────────────────────────── */}
      {/* scrollbar-gutter: stable reserves space for the vertical scrollbar
          so its appearance/disappearance during layout switches doesn't
          shrink the toolbar width and reflow the Publish button into a
          3-line wrap for a couple seconds. */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto" style={{ scrollbarGutter: 'stable' }}>

        {(() => {
          // Hero contact strip data — passed to both editor and preview so the
          // overlay matches the published page. Logo path from biz profile is
          // relative ("/uploads/business_logos/..") so prepend API origin.
          const businessLogoUrl = bizInfo.logo
            ? (/^https?:\/\//i.test(bizInfo.logo) ? bizInfo.logo : `${API_URL || ''}${bizInfo.logo}`)
            : null;
          return showPreview ? (
            <LayoutPreview
              layout={layout} heroImageUrl={heroImageUrl} heroAssetType={heroAssetType} heroHeadline={heroHeadline}
              heroSubheadline={heroSubheadline} sections={sections} accentColor={accentColor}
              ctaText={ctaText} ctaUrl={ctaUrl} onCtaClick={handleCtaClick}
              businessName={bizInfo.name} businessPhone={bizInfo.phone} businessIndustry={bizInfo.industry} businessLogoUrl={businessLogoUrl} />
          ) : (
          <>
            {/* AI-copy starting-point nudge (queue item #13) — shares ONE
                permanent dismiss flag with the caption-editor notice (#14). */}
            <AiCopyNotice className="m-4" />
            {/* Layout-specific editor — each layout has unique structure */}
            <LayoutEditor
              layout={layout}
              heroImageUrl={heroImageUrl} heroImageId={heroImageId} heroAssetType={heroAssetType}
              heroHeadline={heroHeadline} heroSubheadline={heroSubheadline}
              sections={sections} accentColor={accentColor}
              ctaText={ctaText} ctaType={ctaType} ctaUrl={ctaUrl}
              businessName={bizInfo.name} businessPhone={bizInfo.phone} businessIndustry={bizInfo.industry} businessLogoUrl={businessLogoUrl}
              flyerImageUrl={flyerImageUrl}
              onFlyerUpload={(file) => handleUploadImage(file, 'flyer')}
              onFlyerRemove={handleFlyerRemove}
              onHeroHeadlineChange={(v) => { setHeroHeadline(v); debouncedSave({ hero_headline: v }); }}
              onHeroSubheadlineChange={(v) => { setHeroSubheadline(v); debouncedSave({ hero_subheadline: v }); }}
              onHeroPickImage={() => openPexelsFor('hero', '')}
              onHeroUploadImage={(file) => handleUploadImage(file, 'hero')}
              onHeroRemoveImage={() => { setHeroImageId(null); setHeroImageUrl(null); setHeroAssetType('image'); debouncedSave({ hero_image_id: null }); }}
              onUpdateSection={(i, updated) => updateSection(i, updated)}
              onRemoveSection={(i) => removeSection(i)}
              onMoveSection={(i, dir) => moveSection(i, dir)}
              onPickSectionImage={(i) => openPexelsFor({ sectionIndex: i }, sections[i]?.image_search_term || '')}
              onUploadSectionImage={(file, i) => handleUploadImage(file, { sectionIndex: i })}
              onCtaTypeChange={handleCtaTypeChange}
              onCtaTextChange={handleCtaTextChange}
              onCtaUrlChange={handleCtaUrlChange}
              onCtaClick={handleCtaClick}
              debouncedSave={debouncedSave}
              addSectionButtons={
                <div className="flex justify-center gap-3 py-8 bg-gray-50 border-t border-gray-200">
                  <button onClick={addBlankSection}
                    className="px-4 py-2 text-sm font-medium bg-white border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-600 flex items-center gap-2 shadow-sm">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                    Add Blank Section
                  </button>
                  <button onClick={handleExportAsArticle}
                    title="Copy your section text as markdown for your blog (also saves a .md file)"
                    className="px-4 py-2 text-sm font-medium bg-white border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-600 flex items-center gap-2 shadow-sm">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3" /></svg>
                    Export as Article
                  </button>
                  <button onClick={addSectionAI} disabled={addingSectionAI}
                    className="px-4 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 disabled:opacity-50 flex items-center gap-2 shadow-sm"
                    style={{ background: accentColor }}>
                    {addingSectionAI ? (
                      <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Generating...</>
                    ) : (
                      <><svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg> AI Generate Section</>
                    )}
                  </button>
                </div>
              }
            />

            {/* Page slug info */}
            {slug && (
              <div className="text-center text-xs text-gray-400 py-4 bg-gray-50">
                Page URL: ehandshake.com/p/{slug}
              </div>
            )}
          </>
          );
        })()}
      </div>

      {/* ─── Pexels search modal ─────────────────────────────────────────── */}
      {showPexels && (
        <PexelsSearchModal
          subcategory="landing_page"
          initialQuery={pexelsInitialQuery}
          maxSelect={1}
          onImported={handlePexelsImported}
          onClose={() => { setShowPexels(false); setPexelsTarget(null); setPexelsInitialQuery(''); }}
        />
      )}

      {/* ─── Export-as-Article confirmation toast ─────────────────────────── */}
      {exportNotice && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[10000] pointer-events-none">
          <div className={`px-4 py-3 rounded-lg shadow-lg text-sm font-medium text-white pointer-events-auto ${exportNotice.isError ? 'bg-red-600' : 'bg-gray-900'}`}>
            {exportNotice.message}
          </div>
        </div>
      )}

      {/* ─── Contact Us Toast ─────────────────────────────────────────────── */}
      {showContactToast && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowContactToast(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-[420px] max-w-[90vw] overflow-hidden">
            {/* Header */}
            <div className="px-6 pt-5 pb-4 border-b border-gray-100" style={{ background: `${accentColor}08` }}>
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-gray-900">{bizInfo.name || 'Contact Us'}</h3>
                <button onClick={() => setShowContactToast(false)}
                  className="w-7 h-7 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-600 text-lg">
                  &times;
                </button>
              </div>
            </div>
            {/* Body */}
            <div className="px-6 py-5 space-y-3">
              {bizInfo.address && (
                <div className="flex items-start gap-3">
                  <svg className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" />
                  </svg>
                  <div className="text-sm text-gray-700">
                    <div>{bizInfo.address}</div>
                    {(bizInfo.city || bizInfo.state || bizInfo.zip) && (
                      <div>{[bizInfo.city, bizInfo.state].filter(Boolean).join(', ')}{bizInfo.zip ? ` ${bizInfo.zip}` : ''}</div>
                    )}
                  </div>
                </div>
              )}
              {bizInfo.phone && (
                <div className="flex items-center gap-3">
                  <svg className="w-4 h-4 text-gray-400 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 0 1-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 0 0-1.091-.852H4.5A2.25 2.25 0 0 0 2.25 4.5v2.25Z" />
                  </svg>
                  <a href={`tel:${bizInfo.phone.replace(/[^+\d]/g, '')}`}
                    className="text-sm font-medium hover:underline" style={{ color: accentColor }}>
                    {bizInfo.phone}
                  </a>
                </div>
              )}
              {bizInfo.email && (
                <div className="flex items-center gap-3">
                  <svg className="w-4 h-4 text-gray-400 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
                  </svg>
                  <a href={`mailto:${bizInfo.email}`}
                    className="text-sm font-medium hover:underline" style={{ color: accentColor }}>
                    {bizInfo.email}
                  </a>
                </div>
              )}
              {bizInfo.website && (
                <div className="flex items-center gap-3">
                  <svg className="w-4 h-4 text-gray-400 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 0 0 8.716-6.747M12 21a9.004 9.004 0 0 1-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 0 1 7.843 4.582M12 3a8.997 8.997 0 0 0-7.843 4.582m15.686 0A11.953 11.953 0 0 1 12 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0 1 21 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0 1 12 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 0 1 3 12c0-1.605.42-3.113 1.157-4.418" />
                  </svg>
                  <a href={bizInfo.website.startsWith('http') ? bizInfo.website : `https://${bizInfo.website}`}
                    target="_blank" rel="noopener noreferrer"
                    className="text-sm font-medium hover:underline" style={{ color: accentColor }}>
                    {bizInfo.website.replace(/^https?:\/\//, '')}
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
    </div>
  );
}
